import React from "react";
import { Survey, basicValidation, rng } from "../../../amorg-common";
import { SurveyInvitationFile, SurveyInvitationFileUploadError } from "../../../amorg-common/DTOs/SurveyInvitationFile";
import { getLastestUploadFile, sendSurveyInvitationFile, uploadSurveyInvitationFile } from "../../../api/surveyClientApi";
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { Button } from "../../../designSystem/button/Button";
import { H3 } from "../../../designSystem/typography/Heading";
import { P } from "../../../designSystem/typography/Body";
import { FileUploadButton } from '../../../designSystem/formField/FileUploadButton';
import { Card } from "../../../designSystem/Card";
import { TextField } from "../../../designSystem/formField/text/TextField";
import { toDateString } from "../../../utils/date";
import { ButtonRow } from "../../../designSystem/button/ButtonRow";
import { MessageCard } from "../../../designSystem/Message";
import { TextAreaField } from "../../../designSystem/formField/textArea/TextAreaField";
import { colors } from "../../../designSystem/colors/color";

const PageErrorStates = ['file-too-large-error','too-many-lines-error','lines-with-errors','unknown-error'] as const;
type PageErrorState = typeof PageErrorStates[number]
type PageState =  'upload'| 'ready-to-send' | 'invitations-sent-success' | 'invitations-sent-error' | PageErrorState;

type SendInvitationsPanelProps = {
    survey:Survey,
    onClose:()=>void,
    onSent:()=>void
}
export function SendInvitationsPanel(props:SendInvitationsPanelProps):JSX.Element
{
    const [loading, setLoading] = React.useState(false);
    const [pageState, setPageState] = React.useState<PageState>('upload');
    const [file, setFile] = React.useState<SurveyInvitationFile|null>(null);


    async function handleClickUpload(file:File)
    {
        setLoading(true);
        const result = await uploadSurveyInvitationFile(props.survey.id, rng.getRandomId(), file);
        if(result.isSuccess){
            const file = await getLastestUploadFile(props.survey.id);
            setFile(file);
            if(file.lines.some(l => l.error !== null)){
                setPageState('lines-with-errors');
            }
            else {
                setPageState('ready-to-send');
            }
        }
        else{
            if(result.errorMessage === 'surveyInvitationFileUpload_content_too_large')
                setPageState('file-too-large-error');
            else if(result.errorMessage === 'surveyInvitationFileUpload_too_many_lines')
                setPageState('too-many-lines-error');
            else
                setPageState('unknown-error');
        }
        setLoading(false);
    }

    async function handleClickSend(subject:string, body:string)
    {
        const result = await sendSurveyInvitationFile(props.survey.id, file.id, subject, body);
        setPageState(result.isSuccess ? 'invitations-sent-success' : 'invitations-sent-error');
        props.onSent();
    }

    return (
        <>

            <LoadingPageOverlay loading={loading} />

            {pageState==='upload' &&
                <FileUploader
                    onClickUpload={handleClickUpload} 
                    onClose={props.onClose}
                />
            }

            {PageErrorStates.some(x => x === pageState) &&
                <ErrorFileViewer
                    file={file}
                    state={pageState as PageErrorState}
                    onClickUpload={handleClickUpload} 
                    onClose={props.onClose} />
            }

            {pageState==='ready-to-send' &&
                <SendFileViewer
                    survey={props.survey}
                    file={file}
                    onClose={props.onClose}
                    onSend={handleClickSend} />
            }
            {(pageState==='invitations-sent-success' || pageState==='invitations-sent-error') &&
                <FileSentViewer
                    success={pageState === 'invitations-sent-success'}
                    onClose={props.onClose} />
            }
        </>
    );
}

type FileUploaderProps = {
    onClickUpload:(file:File)=>Promise<void>,
    onClose:()=>void    
}
function FileUploader(props:FileUploaderProps):JSX.Element
{
    return (
        <Flex flexDirection="column" gap={20} alignItems="center">
            <Card variant="outlined">
                <Flex flexDirection="column" gap={10}>
                    <div>
                        For at kunne sende invitationer med personlige links til spørgeskemaet, skal du uploade en tekstfil med de pågældende e-mail addresser.
                    </div>
                    <div>
                        <H3>Format</H3>
                        Filen skal være af typen csv eller txt, og indeholde en linie med en gyldig mailaddresse, per invitation der skal sendes.
                    </div>
                    <div>
                        <H3>Eksempel</H3>
                        <P>
                            henriette@amorg.dk<br />
                            henning@amorg.dk<br />
                            ...
                        </P>
                    </div>
                </Flex>
            </Card>

            <Flex flexDirection="column" gap={10}>
                <FileUploadButton
                    inputName='uploaded_file'
                    fileTypes={['csv','txt']}
                    magnum
                    text="Upload fil"
                    onClickUpload={props.onClickUpload} />
            </Flex>

            <div style={{ display:'flex', width:'100%', justifyContent:'end'}}>
                <Button variant="outlined" onClick={props.onClose} text="Annuller" />
            </div>
        </Flex>
    );
}

type LineError = {
    content:string, email:string, error:SurveyInvitationFileUploadError, lineNumber:number
}

type ErrorFileViewerProps = {
    file:SurveyInvitationFile,
    state:PageErrorState,
    onClickUpload:(file:File)=>Promise<void>
    onClose:()=>void
}
function ErrorFileViewer(props:ErrorFileViewerProps):JSX.Element
{
    const allErrors:LineError[] = 
        props.file.lines.map((l,i) => ({lineNumber:i+1, ...l}))
        .filter(l => l.error !== null);

    const shownErrors = allErrors.slice(0, 5);

    function getErrorContent(content:string, email:string, error:SurveyInvitationFileUploadError, lineNumber:number)
    {
        const getComponent = (message:string, lineNumber:number) =>
            <div>
                <div style={{fontSize:'10px', color:colors.text.light}}>Linie {lineNumber}</div>
                <div style={{fontSize:'12px'}}>{message}</div>
            </div>;

        if(error === 'invalid_email' && content.trim() === '')
            return getComponent(`Tom linie`, lineNumber);
        if(error === 'invalid_email')
            return getComponent(`Ugyldig email: ${content}`, lineNumber);
        else if(error === 'email_in_use')
            return getComponent(`Emailadressen er i allerede i brug af for en eksisterende besvarelse: ${email}`, lineNumber);
        else if(error === 'repeated_email')
            return getComponent(`Emailadressen optræder flere gange i filen: ${email}`, lineNumber);

        return getComponent(`Ukendt fejl`, lineNumber);
    }

    console.log('errors\n', JSON.stringify(allErrors, null, 2));
    console.log('shownErrors\n', JSON.stringify(shownErrors, null, 2));

    return (
        <Flex flexDirection="column" gap={20} justifyContent="stretch">

            {props.state === 'file-too-large-error' &&
                <MessageCard variant="error">
                    <P>Filen er for stor. En fil kan maks indeholde 500 rækker data med 50 tegn per linie i gennemsnit.</P>
                </MessageCard>
            }

            {props.state === 'too-many-lines-error' &&
                <MessageCard variant="error">
                    <P>Filen indeholder for mange rækker. En fil kan maks indeholde 500 rækker data.</P>
                </MessageCard>
            }

            {props.state === 'unknown-error' &&
                <MessageCard variant="error">
                    <P>Der opstod en fejl under indlæsning af filen. Prøv igen eller kontakt amorg kundeservice.</P>
                </MessageCard>
            }

            {props.state === 'lines-with-errors' &&
                <>
                    <Card variant="outlined">
                        <H3>Der er fejl i filen</H3>
                        Filen indeholder fejl i {allErrors.length} linie(r). Når fejlen er rettet kan du uploade filen igen.
                    </Card>

                    <Flex flexDirection="column" gap={5} alignItems="stretch" justifyContent="center">
                        {shownErrors.map((e) =>
                            <MessageCard variant="error" key={e.lineNumber}>
                                {getErrorContent(e.content, e.email, e.error, e.lineNumber)}
                            </MessageCard>
                        )}
                        {allErrors.length > 5 &&
                            <span>De først 5 fejl vises...</span>
                        }
                    </Flex>
                </>
            }
            
            <Flex flexDirection="column" gap={5} justifyContent="center" alignItems="center">
                <FileUploadButton
                    inputName='uploaded_file'
                    fileTypes={['csv','txt']}
                    magnum
                    text="Upload fil"
                    onClickUpload={props.onClickUpload} />
            </Flex>

            <div style={{ display:'flex', width:'100%', justifyContent:'end'}}>
                <Button variant="outlined" onClick={props.onClose} text="Annuller" />
            </div>

        </Flex>
    );
}

type SendFileViewerProps = {
    file:SurveyInvitationFile,
    survey:Survey,
    onClose:()=>void,
    onSend:(emailSubject:string, emailBody:string)=>void
}
function SendFileViewer(props:SendFileViewerProps):JSX.Element
{
    const dateString = toDateString(props.survey.end);

    const [subject, setSubject] = React.useState('Din arbejdsgiver inviterer dig til at udfylde et spørgeskema');
    const [subjectError, setSubjectError] = React.useState('');
    const [body, setBody] = React.useState('Kære medarbejder,\n\n'+
    'Da vi ønsker at investere i et sundt og motiverende arbejdsmiljø, vi gerne invitere dig til at deltage en spørgeskemaundersøgelse.\n\n'+
    'Spørgeskemaet er åbent frem til d. '+dateString+', og din personlige besvarelse kan tilgås på nedenstående link.');
    const [bodyError, setBodyError] = React.useState('');

    function handleSend()
    {
        const validSubject = basicValidation.validateTextString(subject, 1, 100);
        const validBody = basicValidation.validateTextString(body, 1, 1000);

        setSubjectError(validSubject ? '' : `Emnefeltet må maks. være på 100 (${subject.length}) tegn og ikke indeholde specialtegn`);
        setBodyError(validBody ? '' : `Indholdsfeltet må maks. være på 1000 (${body.length}) tegn og ikke indeholde specialtegn`);

        if(validSubject && validBody)
            props.onSend(subject, body);
    }


    return (
        <Flex flexDirection="column" gap={20} justifyContent="stretch">
            <MessageCard variant="success">
                <Flex flexDirection="column" gap={10} alignItems="center">
                    <div>
                        <H3>Klar til at sende invitationer</H3>
                        <P>
                            Filen er indlæst, og indeholder {props.file.lines.length} linier med e-mail adresser på modtagere.
                        </P>
                    </div>
                    <P>
                        Når du er klar til at fortsætte kan du indsætte en tekst for emne og indhold på den e-mail, som vi vil sende til modtagerne.
                    </P>
                </Flex>
            </MessageCard>

            <TextField label="Emnefelt" value={subject} onChange={setSubject}
                errorMessage={subjectError} />

            <TextAreaField label="Indhold" value={body} onChange={setBody} minRows={3}
                errorMessage={bodyError} />

            Bemærk at der i slutningen af emailen vil blive indsat et link til modtagerens personlige spørgeskema.

            <ButtonRow>
                <Button variant="outlined" onClick={props.onClose} text="Annuller" />
                <Button variant="filled" onClick={handleSend} text="Send" />
            </ButtonRow>

        </Flex>
    );
}

type FileSentViewerProps = {
    success:boolean,
    onClose:()=>void
}
function FileSentViewer(props:FileSentViewerProps):JSX.Element
{

    return (
        <Flex flexDirection="column" gap={20} justifyContent="stretch">
            {props.success &&
                <MessageCard variant="success">
                    <H3>Invitationer er sendt</H3>
                    <P>
                        Filens modtagere vil om kort tid modtage en e-mail med et link til spørgeskemaet.
                        Du kan nu lukke dette vindue.
                    </P>
                </MessageCard>
            }

            {!props.success &&
                <MessageCard variant="error">
                    <H3>Der opstod en fejl</H3>
                    <P>
                        Filen kunne ikke behandles. Prøv igen eller kontakt amorg kundeservice.
                    </P>
                </MessageCard>
            }

            <ButtonRow>
                <Button variant="outlined" onClick={props.onClose} text="Luk" />
            </ButtonRow>

        </Flex>
    );
}